'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import { useState } from 'react';
import { FiFileText } from 'react-icons/fi';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

export type CertificateGalleryItem = {
  id: number;
  name: string;
  imageUrl?: string;
  fileUrl?: string;
  description?: string;
};

const cardClassName =
  'group flex h-full w-full flex-col items-center gap-3 rounded-[12px] border border-[hsl(240_4%_91%)] bg-white p-2 pb-3 text-center transition hover:-translate-y-0.5 hover:border-[hsl(90_57%_41%)] hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[hsl(90_57%_41%)]';

function Preview({ item }: { item: CertificateGalleryItem }) {
  return (
    <>
      <span className="relative block aspect-[3/4] w-full overflow-hidden rounded-[8px] bg-[hsl(231_47%_97%)]">
        {item.imageUrl ? (
          <Image
            src={item.imageUrl}
            // The caption below names the certificate.
            alt=""
            fill
            className="object-contain"
            sizes={buildImageSizes([
              { min: '1024px', size: '150px' },
              { min: '640px', size: '25vw' },
              { size: '45vw' },
            ])}
            loading="lazy"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-[hsl(90_57%_41%)]">
            <FiFileText size={40} aria-hidden />
          </span>
        )}
      </span>
      <span className="px-1 text-xs font-bold leading-snug text-foreground sm:text-[13px]">
        {item.name}
      </span>
    </>
  );
}

/**
 * Certificate thumbnails with a zoomable lightbox. One row on desktop, a swipeable row on
 * phones and tablets. An item with a PDF and no image opens the PDF in a new tab instead.
 */
export function CertificatesGallery({
  items,
  openLabel,
}: {
  items: CertificateGalleryItem[];
  openLabel: string;
}) {
  const [openIndex, setOpenIndex] = useState(-1);
  const slides = items
    .filter(item => item.imageUrl)
    .map(item => ({
      src: item.imageUrl!,
      alt: item.name,
      title: item.name,
      description: item.description,
    }));

  return (
    <>
      <ul className="-mx-6 flex w-[calc(100%+48px)] snap-x snap-mandatory scroll-px-6 gap-4 overflow-x-auto px-6 pb-2 lg:mx-0 lg:w-full lg:flex-wrap lg:justify-center lg:overflow-visible lg:px-0 lg:pb-0">
        {items.map(item => {
          const slideIndex = slides.findIndex(slide => slide.src === item.imageUrl);

          return (
            <li
              key={item.id}
              className="w-[42%] shrink-0 snap-start sm:w-[28%] md:w-[22%] lg:w-[calc((100%-7*16px)/8)]"
            >
              {slideIndex >= 0 ? (
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-label={`${item.name} – ${openLabel}`}
                  className={cardClassName}
                  onClick={() => setOpenIndex(slideIndex)}
                >
                  <Preview item={item} />
                </button>
              ) : item.fileUrl ? (
                <a
                  href={item.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${item.name} – ${openLabel} (PDF)`}
                  className={cardClassName}
                >
                  <Preview item={item} />
                </a>
              ) : (
                <div className={cardClassName}>
                  <Preview item={item} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <Lightbox
        open={openIndex >= 0}
        index={Math.max(openIndex, 0)}
        close={() => setOpenIndex(-1)}
        slides={slides}
        plugins={[Captions, Zoom]}
        captions={{ descriptionTextAlign: 'center' }}
        zoom={{ maxZoomPixelRatio: 2 }}
        // Above the sticky header and the cookie/chat widgets.
        styles={{ root: { zIndex: 999999 } }}
      />
    </>
  );
}
